import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { updateDistributorSchema } from '@lombok/shared';
import type { Distributor, UpdateDistributorInput } from '@lombok/shared';
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from '@lombok/ui';
import { PageHeader } from '@/components/PageHeader';
import { BackLink, DetailRow } from '@/components/DetailRow';
import { FORM_COLUMN } from '@/components/layout';
import { useIsAdmin } from '@/auth/useIsAdmin';
import { StatusPill } from '@/components/StatusPill';
import { QueryState } from '@/components/QueryState';
import { FormActions, FormError, TextField } from '@/components/form';
import { TagsField } from '@/components/form/TagsField';
import { AccountStatusPanel } from '@/features/accounts/AccountStatusPanel';
import { useChangeDistributorStatus, useDistributor, useUpdateDistributor } from '@/data/hooks';
import { placeLabel } from '@/lib/names';

const BASE = '/app/admin/distribuidores';

/**
 * Distribuidor detail (admin) with inline edit. Read mode shows the account
 * summary; "Editar" swaps to an RHF form validated by the shared
 * `updateDistributorSchema`. The backend re-authorizes and re-validates.
 */
export function DistributorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const query = useDistributor(id);
  const isAdmin = useIsAdmin();
  const [editing, setEditing] = useState(false);

  return (
    <div className={`flex flex-col gap-6 ${FORM_COLUMN}`}>
      <div className="flex flex-col gap-2">
        <BackLink to={BASE}>Distribuidores</BackLink>
        <PageHeader
          title={query.data?.name ?? 'Distribuidor'}
          description="Cuenta mayorista, territorios y ubicaciones."
          actions={
            query.data && !editing && isAdmin ? (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Editar
              </Button>
            ) : undefined
          }
        />
      </div>

      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        error={query.error}
        onRetry={() => void query.refetch()}
        loadingLabel="Cargando distribuidor…"
      >
        {query.data ? (
          <div className="flex flex-col gap-6">
            <DistributorStatus distributor={query.data} />
            {editing ? (
              <DistributorEditForm distributor={query.data} onDone={() => setEditing(false)} />
            ) : (
              <DistributorSummary distributor={query.data} />
            )}
          </div>
        ) : null}
      </QueryState>
    </div>
  );
}

function DistributorStatus({ distributor: d }: { distributor: Distributor }) {
  const change = useChangeDistributorStatus(d.distributorId);
  return (
    <AccountStatusPanel
      accountId={d.distributorId}
      status={d.status}
      statusReason={d.statusReason}
      onChange={(input) => change.mutateAsync(input)}
    />
  );
}

function DistributorSummary({ distributor: d }: { distributor: Distributor }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Información de la cuenta</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <dl className="flex flex-col">
          <DetailRow label="Nombre">{d.name}</DetailRow>
          <DetailRow label="RIF">{d.rif}</DetailRow>
          <DetailRow label="Estado">
            <StatusPill kind="account" value={d.status} />
          </DetailRow>
          <DetailRow label="Territorios">
            <TagList values={d.territoryIds} empty="Sin territorios" />
          </DetailRow>
          <DetailRow label="Ubicaciones permitidas">
            <TagList values={d.allowedLocationIds} empty="Sin ubicaciones" />
          </DetailRow>
        </dl>
      </CardContent>
    </Card>
  );
}

function TagList({ values, empty }: { values: string[]; empty: string }) {
  if (values.length === 0) return <span className="text-text-muted">{empty}</span>;
  return (
    <span className="flex flex-wrap justify-end gap-1.5">
      {values.map((v) => (
        <Badge key={v} tone="accent">
          {placeLabel(v)}
        </Badge>
      ))}
    </span>
  );
}

function DistributorEditForm({
  distributor: d,
  onDone,
}: {
  distributor: Distributor;
  onDone: () => void;
}) {
  const mutation = useUpdateDistributor(d.distributorId);

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<UpdateDistributorInput>({
    resolver: zodResolver(updateDistributorSchema),
    defaultValues: {
      name: d.name,
      rif: d.rif,
      territoryIds: d.territoryIds,
      allowedLocationIds: d.allowedLocationIds,
    },
  });

  // Keep the form in sync if the underlying record changes (e.g. background refetch).
  useEffect(() => {
    reset({
      name: d.name,
      rif: d.rif,
      territoryIds: d.territoryIds,
      allowedLocationIds: d.allowedLocationIds,
    });
  }, [d, reset]);

  const onSubmit = handleSubmit((values) => {
    mutation.mutate(values, { onSuccess: () => onDone() });
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Editar cuenta</CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        <form onSubmit={onSubmit} className="flex flex-col gap-5" noValidate>
          <FormError error={mutation.error} />

          <TextField label="Nombre" required error={errors.name?.message} {...register('name')} />
          <TextField label="RIF" required error={errors.rif?.message} {...register('rif')} />

          <Controller
            control={control}
            name="territoryIds"
            render={({ field, fieldState }) => (
              <TagsField
                label="Territorios"
                inputId="territoryIds"
                value={field.value ?? []}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="allowedLocationIds"
            render={({ field, fieldState }) => (
              <TagsField
                label="Ubicaciones permitidas"
                inputId="allowedLocationIds"
                value={field.value ?? []}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />

          <FormActions>
            <Button type="button" variant="outline" onClick={onDone}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending || !isDirty}>
              {mutation.isPending ? 'Guardando…' : 'Guardar cambios'}
            </Button>
          </FormActions>
        </form>
      </CardContent>
    </Card>
  );
}
